import { useEffect, useRef, useState } from 'react';
import { IconArrowDown } from '@tabler/icons-react';
import {
  ActionIcon,
  Box,
  Button,
  Modal,
  ScrollArea,
  Stack,
  Text,
  useMatches,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { PRIVACY_POLICY_V1 } from '../_constants/privacy-and-policy-v1';
import FileHeading from './file-heading';

type privacyAndPolicyProps = {
  onAgreementChange?: (value: boolean) => void;
  openModal: boolean;
  setOpenModal: (value: boolean) => void;
};

export function PrivacyAndPolicy({
  onAgreementChange,
  openModal,
  setOpenModal,
}: privacyAndPolicyProps) {
  const [opened, { open, close }] = useDisclosure(false);
  const [bottomReached, setBottomReached] = useState(false);
  const viewportRef = useRef<HTMLDivElement>(null);
  const isMobileView = useMatches({ base: true, md: false });

  const scrollToBottom = () => {
    const el = viewportRef.current;
    if (el) {
      el.scrollTo({
        top: el.scrollHeight,
        behavior: 'smooth',
      });
    }
  };

  const handleAgreementChange = (value: boolean) => {
    onAgreementChange?.(value);
    close();
  };

  useEffect(() => {
    if (openModal && !opened) {
      open();
      setBottomReached(false);
      setOpenModal(false);
    }
  }, [openModal, opened]);

  return (
    <>
      <Modal
        opened={opened}
        onClose={close}
        centered
        size={isMobileView ? '100%' : '60%'}
      >
        <Box pos="relative">
          <ScrollArea
            h={{ base: 750, md: 650 }}
            viewportRef={viewportRef}
            onScrollPositionChange={({ y }) => {
              const el = viewportRef.current;
              if (!el) {
                return;
              }

              const distanceFromBottom =
                el.scrollHeight - (y + el.clientHeight);

              if (distanceFromBottom <= 50) {
                setBottomReached(true);
              } else {
                setBottomReached(false);
              }
            }}
          >
            {PRIVACY_POLICY_V1.map((section, idx) => (
              <Stack key={idx} gap="md" pr="md" pl="sm">
                <Text fw="bold" fz="md" ta="center" className="leading-8">
                  {section.main_title}
                </Text>

                <Text ta="justify" className="leading-8">
                  {section.description}
                </Text>
                <Text ta="justify" className="leading-8">
                  {section.sub_description}
                </Text>

                {section.chapter.map((h, hIdx) => (
                  <Stack key={hIdx} gap="0">
                    <FileHeading heading={h.header} />

                    {h.content.map((c, cIdx) => (
                      <Box key={cIdx}>{c.content}</Box>
                    ))}
                  </Stack>
                ))}

                <FileHeading heading={section.agreement_statement_title} />
                <Text ta="justify" mb="md" pl={5}>
                  {section.agreement_statement}
                </Text>
              </Stack>
            ))}

            {onAgreementChange && (
              <Button
                fullWidth
                mt="2rem"
                size="md"
                onClick={() => handleAgreementChange(true)}
              >
                Saya Menyetujui
              </Button>
            )}
          </ScrollArea>
          {!bottomReached && (
            <ActionIcon
              onClick={scrollToBottom}
              color="brand"
              radius="xl"
              size={50}
              pos="absolute"
              right={20}
              style={{
                bottom: 'calc(env(safe-area-inset-bottom) + 20px)',
                zIndex: 999,
                opacity: 0.9,
              }}
            >
              <IconArrowDown stroke={1.5} size={30} />
            </ActionIcon>
          )}
        </Box>
      </Modal>
    </>
  );
}
